import { create } from 'zustand';
import { apiUrl } from '../api/api-url';
import { appClient } from '../api/http';
import type { BootstrapState } from '../api/types';

interface RegistrationAvailabilityState {
  /** The bootstrap door: true only while the instance has no account at all. */
  readonly open: boolean;
  /** The self-service door: the administrator-held open-signups switch. */
  readonly signUpOpen: boolean;
}

/**
 * The anonymous discovery read (014 research R-010): one `GET /api/v1/auth/bootstrap` behind both
 * the sign-in page's conditional "Create an account" link and `/sign-up`'s guard.
 *
 * **The in-flight promise is shared, then dropped** — concurrent callers ride one request (013's
 * `container-lookups.ts` lesson), but once it settles the next caller re-reads. Latching the first
 * answer for the whole SPA session was wrong twice over (code review 2026-08-27): a transient
 * failure hid the sign-up door until a reload, and a bootstrap that closed the door left /sign-in
 * still offering it. The answer is advisory either way, because both creation routes re-read their
 * gates at execution time (FR-013) — nothing here decides what succeeds, only what is offered.
 *
 * **A failed read answers *closed*, and `loadAvailability()` never rejects.** If the call fails we
 * do not know whether a door exists; the safe answer is no link and a redirect, because an offer
 * that 403s is worse than no offer. The sign-in screen itself is unaffected and still works.
 */
const store = create<RegistrationAvailabilityState>()(() => ({ open: false, signUpOpen: false }));

export const bootstrapOpen = { use: (): boolean => store((s) => s.open), get: (): boolean => store.getState().open };
export const signUpOpen = { use: (): boolean => store((s) => s.signUpOpen), get: (): boolean => store.getState().signUpOpen };

/** The read in flight, if any; concurrent callers share it, and it is dropped once settled. */
let reading: Promise<void> | null = null;

async function read(): Promise<void> {
  try {
    const state = await appClient().get<BootstrapState>(apiUrl('/api/v1/auth/bootstrap'));
    store.setState({ open: state.open, signUpOpen: state.signUpOpen });
  } catch {
    // Deliberately swallowed: see the header. Both doors stay closed.
    store.setState({ open: false, signUpOpen: false });
  }
}

/** Concurrent callers share one request; a later caller issues a fresh one. */
export function loadAvailability(): Promise<void> {
  return (reading ??= read().finally(() => {
    reading = null;
  }));
}

/** Test seam, like the other stores'. Production code never calls it. */
export function resetAvailability(): void {
  store.setState({ open: false, signUpOpen: false });
  reading = null;
}
